import DashboardLayout from '@/components/DashboardLayout';
import { useForm } from '@inertiajs/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Package, DollarSign, Hash, Repeat, Calendar } from 'lucide-react';
import { useState, useEffect } from 'react';

const WEEKS_PER_MONTH = 4.33;

const CYCLE_LABELS: Record<string, { label: string; months: number }> = {
  monthly: { label: 'Mensal', months: 1 },
  semestral: { label: 'Semestral', months: 6 },
  annual: { label: 'Anual', months: 12 },
};

const formatBRL = (n: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n);

interface LessonPackage {
  id: number;
  name: string;
  description: string;
  lessons_count: number;
  price: string;
  validity_days: number | null;
  active: boolean;
  generates_acquisition_lead: boolean;
  billing_cycle: string;
  duration_months: number | null;
  lessons_per_week: number | null;
  monthly_price: string | null;
}

interface Props {
  lesson_package: LessonPackage;
  existing_lead_package: { id: number; name: string } | null;
}

export default function Edit({ lesson_package, existing_lead_package }: Props) {
  const initialIsRecurring = lesson_package.billing_cycle && lesson_package.billing_cycle !== 'one_time';
  const [planType, setPlanType] = useState<'one_time' | 'recurring'>(
    initialIsRecurring ? 'recurring' : 'one_time'
  );

  const { data, setData, patch, processing, errors } = useForm({
    name: lesson_package.name || '',
    description: lesson_package.description || '',
    lessons_count: lesson_package.lessons_count?.toString() || '',
    price: lesson_package.price || '',
    validity_days: lesson_package.validity_days?.toString() || '',
    active: lesson_package.active ?? true,
    generates_acquisition_lead: lesson_package.generates_acquisition_lead ?? false,
    billing_cycle: lesson_package.billing_cycle || 'one_time',
    lessons_per_week: lesson_package.lessons_per_week?.toString() || '',
    monthly_price: lesson_package.monthly_price || '',
  });

  const [displayPrice, setDisplayPrice] = useState('');
  const [displayMonthlyPrice, setDisplayMonthlyPrice] = useState('');

  useEffect(() => {
    if (lesson_package.price) {
      setDisplayPrice(lesson_package.price.replace('.', ','));
    }
    if (lesson_package.monthly_price) {
      setDisplayMonthlyPrice(lesson_package.monthly_price.replace('.', ','));
    }
  }, [lesson_package.price, lesson_package.monthly_price]);

  const handleMoneyChange =
    (setDisplay: (v: string) => void, field: 'price' | 'monthly_price') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const cleaned = e.target.value.replace(/[^\d,]/g, '');
      const parts = cleaned.split(',');
      const formatted = parts.length > 1 ? parts[0] + ',' + parts[1].slice(0, 2) : cleaned;
      setDisplay(formatted);
      setData(field, formatted.replace(',', '.'));
    };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    patch(`/lesson_packages/${lesson_package.id}`);
  };

  const cycleInfo = CYCLE_LABELS[data.billing_cycle];
  const months = cycleInfo?.months ?? 0;
  const lpw = parseInt(data.lessons_per_week) || 0;
  const monthlyP = parseFloat(data.monthly_price) || 0;
  const totalLessons = months && lpw ? Math.round(months * WEEKS_PER_MONTH * lpw) : 0;
  const totalContract = months * monthlyP;

  const oneTimePerLesson =
    data.lessons_count && data.price
      ? formatBRL(parseFloat(data.price) / parseInt(data.lessons_count))
      : 'R$ 0,00';

  const handlePlanTypeChange = (value: 'one_time' | 'recurring') => {
    setPlanType(value);
    if (value === 'one_time') {
      setData((d) => ({
        ...d,
        billing_cycle: 'one_time',
        lessons_per_week: '',
        monthly_price: '',
      }));
      setDisplayMonthlyPrice('');
    } else {
      setData((d) => ({
        ...d,
        billing_cycle: d.billing_cycle === 'one_time' ? 'monthly' : d.billing_cycle,
      }));
    }
  };

  return (
    <DashboardLayout>
      <div className="p-8 max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Package className="w-5 h-5 text-primary" />
              </div>
              Editar Pacote de Aulas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Pacote *</Label>
                <Input
                  id="name"
                  placeholder="Ex: Pacote 5 Aulas, Anual 2x na semana, etc."
                  value={data.name}
                  onChange={(e) => setData('name', e.target.value)}
                />
                {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  placeholder="Descreva o que está incluído neste pacote..."
                  rows={3}
                  value={data.description}
                  onChange={(e) => setData('description', e.target.value)}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description}</p>
                )}
              </div>

              <div className="space-y-3">
                <Label>Tipo de Plano *</Label>
                <RadioGroup
                  value={planType}
                  onValueChange={(v) => handlePlanTypeChange(v as 'one_time' | 'recurring')}
                  className="grid grid-cols-2 gap-3"
                >
                  <Label
                    htmlFor="plan-onetime"
                    className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      planType === 'one_time'
                        ? 'border-primary bg-primary/5'
                        : 'border-input hover:bg-accent/50'
                    }`}
                  >
                    <RadioGroupItem value="one_time" id="plan-onetime" className="mt-0.5" />
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 font-medium">
                        <Package className="w-4 h-4" />
                        Avulso / Pacote fechado
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Quantidade fixa de aulas (ex.: 10 aulas por R$ 800)
                      </p>
                    </div>
                  </Label>
                  <Label
                    htmlFor="plan-recurring"
                    className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      planType === 'recurring'
                        ? 'border-primary bg-primary/5'
                        : 'border-input hover:bg-accent/50'
                    }`}
                  >
                    <RadioGroupItem value="recurring" id="plan-recurring" className="mt-0.5" />
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2 font-medium">
                        <Repeat className="w-4 h-4" />
                        Plano recorrente
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Por duração + frequência semanal (ex.: Anual 2x/semana)
                      </p>
                    </div>
                  </Label>
                </RadioGroup>
              </div>

              {planType === 'one_time' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="lessons_count">Número de Aulas *</Label>
                      <div className="relative">
                        <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="lessons_count"
                          type="number"
                          min="1"
                          placeholder="Digite o número de aulas"
                          className="pl-10"
                          value={data.lessons_count}
                          onChange={(e) => setData('lessons_count', e.target.value)}
                        />
                      </div>
                      {errors.lessons_count && (
                        <p className="text-sm text-destructive">{errors.lessons_count}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="price">Preço Total *</Label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="price"
                          type="text"
                          inputMode="decimal"
                          placeholder="Digite o preço total"
                          className="pl-10"
                          value={displayPrice}
                          onChange={handleMoneyChange(setDisplayPrice, 'price')}
                        />
                      </div>
                      {errors.price && <p className="text-sm text-destructive">{errors.price}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Validade do Pacote</Label>
                    <Select
                      value={data.validity_days || 'none'}
                      onValueChange={(value) =>
                        setData(
                          'validity_days',
                          value === 'none' ? '' : value === 'custom' ? 'custom' : value
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a validade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sem validade</SelectItem>
                        <SelectItem value="15">15 dias</SelectItem>
                        <SelectItem value="30">1 mês (30 dias)</SelectItem>
                        <SelectItem value="60">2 meses (60 dias)</SelectItem>
                        <SelectItem value="90">3 meses (90 dias)</SelectItem>
                        <SelectItem value="180">6 meses (180 dias)</SelectItem>
                        <SelectItem value="365">1 ano (365 dias)</SelectItem>
                        <SelectItem value="custom">Personalizado...</SelectItem>
                      </SelectContent>
                    </Select>
                    {data.validity_days === 'custom' && (
                      <Input
                        type="number"
                        min="1"
                        placeholder="Digite o número de dias"
                        onChange={(e) => setData('validity_days', e.target.value)}
                      />
                    )}
                    <p className="text-xs text-muted-foreground">
                      {data.validity_days && data.validity_days !== 'custom'
                        ? `O aluno terá ${data.validity_days} dias para usar as aulas a partir da compra`
                        : 'Sem validade — o aluno pode usar as aulas quando quiser'}
                    </p>
                  </div>

                  {data.lessons_count && data.price && parseFloat(data.price) > 0 && (
                    <div className="p-4 bg-primary/10 rounded-lg">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Preço por aula:</span>
                        <span className="text-xl font-bold text-primary">{oneTimePerLesson}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {planType === 'recurring' && (
                <div className="space-y-4 p-4 border rounded-lg bg-accent/20">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Duração *</Label>
                      <Select
                        value={data.billing_cycle === 'one_time' ? 'monthly' : data.billing_cycle}
                        onValueChange={(v) => setData('billing_cycle', v)}
                      >
                        <SelectTrigger>
                          <Calendar className="w-4 h-4 mr-2" />
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Mensal (1 mês)</SelectItem>
                          <SelectItem value="semestral">Semestral (6 meses)</SelectItem>
                          <SelectItem value="annual">Anual (12 meses)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Frequência semanal *</Label>
                      <Select
                        value={data.lessons_per_week}
                        onValueChange={(v) => setData('lessons_per_week', v)}
                      >
                        <SelectTrigger>
                          <Repeat className="w-4 h-4 mr-2" />
                          <SelectValue placeholder="Aulas por semana" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1x na semana</SelectItem>
                          <SelectItem value="2">2x na semana</SelectItem>
                          <SelectItem value="3">3x na semana</SelectItem>
                          <SelectItem value="4">4x na semana</SelectItem>
                          <SelectItem value="5">5x na semana</SelectItem>
                          <SelectItem value="6">6x na semana</SelectItem>
                          <SelectItem value="7">7x na semana</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.lessons_per_week && (
                        <p className="text-sm text-destructive">{errors.lessons_per_week}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="monthly_price">Valor mensal *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="monthly_price"
                        type="text"
                        inputMode="decimal"
                        placeholder="Digite o valor mensal"
                        className="pl-10"
                        value={displayMonthlyPrice}
                        onChange={handleMoneyChange(setDisplayMonthlyPrice, 'monthly_price')}
                      />
                    </div>
                    {errors.monthly_price && (
                      <p className="text-sm text-destructive">{errors.monthly_price}</p>
                    )}
                  </div>

                  {totalLessons > 0 && monthlyP > 0 && (
                    <div className="p-4 bg-primary/10 rounded-lg space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {months} {months === 1 ? 'mês' : 'meses'} × {WEEKS_PER_MONTH} semanas × {lpw}{' '}
                          {lpw === 1 ? 'aula' : 'aulas'}/sem
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Total de aulas:</span>
                        <span className="text-xl font-bold text-primary">~{totalLessons} aulas</span>
                      </div>
                      <div className="flex items-center justify-between border-t pt-2">
                        <span className="font-medium">Valor total do contrato:</span>
                        <span className="text-xl font-bold text-primary">
                          {formatBRL(totalContract)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground pt-1">
                        Validade: {months * 30} dias a partir da compra
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div
                className={`flex items-center justify-between p-4 border rounded-lg ${
                  existing_lead_package
                    ? 'border-muted bg-muted/30 opacity-60'
                    : 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950/30'
                }`}
              >
                <div className="space-y-0.5">
                  <Label htmlFor="generates_acquisition_lead">Pacote de Aula Experimental</Label>
                  <p className="text-sm text-muted-foreground">
                    {existing_lead_package
                      ? `Já existe um pacote experimental ativo: "${existing_lead_package.name}". Desative-o antes de ativar outro.`
                      : 'Ao comprar este pacote, o aluno entra automaticamente no CRM como lead'}
                  </p>
                </div>
                <Switch
                  id="generates_acquisition_lead"
                  checked={data.generates_acquisition_lead}
                  onCheckedChange={(checked) => setData('generates_acquisition_lead', checked)}
                  disabled={!!existing_lead_package}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="space-y-0.5">
                  <Label htmlFor="active">Pacote Ativo</Label>
                  <p className="text-sm text-muted-foreground">
                    Pacotes inativos não aparecem para venda
                  </p>
                </div>
                <Switch
                  id="active"
                  checked={data.active}
                  onCheckedChange={(checked) => setData('active', checked)}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.history.back()}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="flex-1" disabled={processing}>
                  {processing ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
